/**
 * Mute Button Component
 *
 * Walkie-talkie style toggle for muting incoming voice messages.
 */

import { clsx } from 'clsx'

interface MuteButtonProps {
  isMuted: boolean
  onToggle: () => void
  className?: string
}

export function MuteButton({ isMuted, onToggle, className }: MuteButtonProps) {
  return (
    <button
      onClick={onToggle}
      className={clsx(
        'group relative flex flex-col items-center justify-center transition-all',
        'w-14 h-14 rounded-lg border-2',
        // Tactical "Knurled" Texture via CSS gradients
        'bg-[#1a1a1a]',
        'bg-[radial-gradient(circle_at_center,_transparent_0%,_#000_100%),repeating-linear-gradient(45deg,_rgba(255,255,255,0.03)_0px,_rgba(255,255,255,0.03)_1px,_transparent_1px,_transparent_4px),repeating-linear-gradient(-45deg,_rgba(255,255,255,0.03)_0px,_rgba(255,255,255,0.03)_1px,_transparent_1px,_transparent_4px)]',
        isMuted
          ? 'border-red-500/50 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8),0_0_15px_rgba(239,68,68,0.2)]'
          : 'border-green-500/50 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8),0_0_15px_rgba(34,197,94,0.2)]',
        'hover:scale-105 active:scale-95',
        className
      )}
      title={isMuted ? 'Unmute incoming audio' : 'Mute incoming audio'}
    >
      {/* Inner LED indicator */}
      <div
        className={clsx(
          'absolute top-1.5 w-6 h-0.5 rounded-full transition-all duration-200',
          isMuted
            ? "bg-red-500 shadow-[0_0_8px_theme('colors.red.500')]"
            : "bg-green-500 shadow-[0_0_8px_theme('colors.green.500')]"
        )}
      />

      {/* Icon */}
      <span
        className={clsx(
          'material-icons-round text-2xl transition-all duration-100 mt-1',
          isMuted
            ? 'text-red-500 drop-shadow-[0_0_6px_rgba(239,68,68,0.6)]'
            : 'text-green-500 drop-shadow-[0_0_6px_rgba(34,197,94,0.6)]'
        )}
      >
        {isMuted ? 'volume_off' : 'volume_up'}
      </span>

      {/* Label */}
      <span
        className={clsx(
          'font-mono text-[7px] font-bold tracking-[0.15em] leading-none mt-0.5 transition-colors uppercase',
          isMuted ? 'text-red-500/80' : 'text-green-500/80'
        )}
      >
        {isMuted ? 'MUTED' : 'RX ON'}
      </span>

      {/* Inner ring decoration */}
      <div
        className={clsx(
          'absolute inset-0.5 rounded-md border border-dashed pointer-events-none transition-colors opacity-30',
          isMuted ? 'border-red-500/50' : 'border-green-500/50'
        )}
      />
    </button>
  )
}
